import React, { useEffect, useState } from 'react';
import { Modal, Row, Col, Spinner } from 'react-bootstrap';
import DOMPurify from 'isomorphic-dompurify';
import { apiHandler } from '../../api/apihandler';
import { TEACHERS_ALLERGY_VIEW_API } from '../../api/constants';
import { formatDisplayDate } from '../../utilities/displayFormat';

// Shared "Allergy & Food Preference" modal - the real one, previously only
// reachable from the Attendance page's per-student action button. Also
// rendered from notification.js so a teacher clicking an Allergy Action Plan
// notification sees this exact modal instead of a separate, limited one.
// Self-contained: fetches its own data from studentId rather than depending
// on the caller's other state.
const AllergyModal = ({ show, onHide, studentId, studentName }) => {
  const [allergyData, setAllergyData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !studentId) {
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setAllergyData(null);
      try {
        const response = await apiHandler(TEACHERS_ALLERGY_VIEW_API, 'POST', {
          student_id: studentId,
        });
        if (cancelled) return;
        if (response.data?.response?.status === 200) {
          setAllergyData(response.data?.data || {});
        }
      } catch (error) {
        console.error('Error loading allergy report:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, studentId]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered style={{ marginTop: '50px', marginBottom: '50px' }}>
      <Modal.Header closeButton style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '16px 20px' }}>
        <Modal.Title style={{ fontSize: '15px', fontWeight: '700', color: '#111827', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ background: '#f3f4f6', borderRadius: '8px', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="ri-alert-fill" style={{ fontSize: '18px', color: '#f59e0b' }}></i>
          </div>
          Allergy & Food Preference
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '16px', background: '#f9fafb', maxHeight: '70vh', overflowY: 'auto' }}>
        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading...
          </div>
        ) : allergyData && allergyData.student ? (
          <>
            <div style={{ marginBottom: '12px', fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
              <strong>Student:</strong> {allergyData.student.name}
            </div>

            {allergyData.allergy_report && (
              <div style={{ background: 'white', padding: '16px', borderRadius: '8px', marginBottom: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h6 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', color: '#374151', gap: '8px' }}>
                  <i className="ri-alert-line" style={{ fontSize: '16px', color: '#6366f1' }}></i>
                  Allergy & Food Preference - {allergyData.student.name}
                </h6>

                <Row>
                  {/* Left Side - Allergies */}
                  <Col md={6}>
                    <div style={{ background: '#fef3c7', padding: '12px', borderRadius: '6px', marginBottom: '10px', border: '1px solid #fde68a' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: '#92400e' }}>
                        ⚠️ Allergies
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {allergyData.allergy_report.is_peanuts === 1 && (
                          <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                            <span style={{ color: '#92400e', marginRight: '6px' }}>•</span>
                            <span>Peanuts</span>
                          </div>
                        )}
                        {allergyData.allergy_report.is_shellfish === 1 && (
                          <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                            <span style={{ color: '#92400e', marginRight: '6px' }}>•</span>
                            <span>Shellfish {allergyData.allergy_report.shellfish_specify ? `(${allergyData.allergy_report.shellfish_specify})` : ''}</span>
                          </div>
                        )}
                        {allergyData.allergy_report.is_tree_nuts === 1 && (
                          <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                            <span style={{ color: '#92400e', marginRight: '6px' }}>•</span>
                            <span>Tree Nuts</span>
                          </div>
                        )}
                        {allergyData.allergy_report.is_fish === 1 && (
                          <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                            <span style={{ color: '#92400e', marginRight: '6px' }}>•</span>
                            <span>Fish {allergyData.allergy_report.fish_specify ? `(${allergyData.allergy_report.fish_specify})` : ''}</span>
                          </div>
                        )}
                        {allergyData.allergy_report.is_milk === 1 && (
                          <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                            <span style={{ color: '#92400e', marginRight: '6px' }}>•</span>
                            <span>Milk/Dairy</span>
                          </div>
                        )}
                        {allergyData.allergy_report.all_dairy_products === 1 && (
                          <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                            <span style={{ color: '#92400e', marginRight: '6px' }}>•</span>
                            <span>All Dairy Products</span>
                          </div>
                        )}
                        {allergyData.allergy_report.is_food_additives === 1 && (
                          <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                            <span style={{ color: '#92400e', marginRight: '6px' }}>•</span>
                            <span>Food Additives {allergyData.allergy_report.food_additives_specify ? `(${allergyData.allergy_report.food_additives_specify})` : ''}</span>
                          </div>
                        )}
                        {allergyData.allergy_report.insect_stings_bites === 1 && (
                          <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                            <span style={{ color: '#92400e', marginRight: '6px' }}>•</span>
                            <span>Insect Stings/Bites {allergyData.allergy_report.insect_stings_bites_specify ? `(${allergyData.allergy_report.insect_stings_bites_specify})` : ''}</span>
                          </div>
                        )}
                        {allergyData.allergy_report.is_other === 1 && (
                          <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                            <span style={{ color: '#92400e', marginRight: '6px' }}>•</span>
                            <span>Other {allergyData.allergy_report.other_specify ? `(${allergyData.allergy_report.other_specify})` : ''}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* Right Side - Emergency Treatment */}
                  <Col md={6}>
                    <div style={{ background: '#fee2e2', padding: '12px', borderRadius: '6px', marginBottom: '10px', border: '1px solid #fecaca' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: '#991b1b' }}>
                        🏥 Emergency Treatment
                      </div>

                      {allergyData.allergy_report.emergency_treatment_procedures && (
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#991b1b', marginBottom: '4px' }}>
                            Procedures:
                          </div>
                          <div style={{ fontSize: '12px', color: '#374151' }}>
                            {allergyData.allergy_report.emergency_treatment_procedures}
                          </div>
                        </div>
                      )}

                      {allergyData.allergy_report.insect_stings_bites === 1 && allergyData.allergy_report.insect_stings_bites_medication && (
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#991b1b', marginBottom: '4px' }}>
                            Insect Sting/Bite Medication:
                          </div>
                          <div style={{ fontSize: '12px', color: '#374151' }}>
                            {allergyData.allergy_report.insect_stings_bites_medication}
                          </div>
                        </div>
                      )}

                      {allergyData.allergy_report.require_epi_pen === 1 && (
                        <div style={{ background: '#fecaca', padding: '8px', borderRadius: '6px', marginBottom: '8px', border: '1px solid #fca5a5' }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#991b1b', marginBottom: '4px' }}>
                            💉 EpiPen Required
                          </div>
                          {allergyData.allergy_report.location_of_epi_pen && (
                            <div style={{ fontSize: '11px', color: '#991b1b' }}>
                              Location: {allergyData.allergy_report.location_of_epi_pen}
                            </div>
                          )}
                          {allergyData.allergy_report.location_of_epi_pen_at_daycare && (
                            <div style={{ fontSize: '11px', color: '#991b1b', marginTop: '4px' }}>
                              Location at Daycare: {allergyData.allergy_report.location_of_epi_pen_at_daycare}
                            </div>
                          )}
                        </div>
                      )}

                      {allergyData.allergy_report.symptoms && (
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#991b1b', marginBottom: '4px' }}>
                            Symptoms:
                          </div>
                          <div style={{ fontSize: '12px', color: '#374151' }}>
                            {allergyData.allergy_report.symptoms}
                          </div>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                {/* Signatures */}
                <Row style={{ marginTop: '12px' }}>
                  <Col md={6}>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                        Parent Signature:
                      </div>
                      {allergyData.allergy_report.parent_signature ? (
                        <div>
                          <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '8px', background: '#f9fafb', marginBottom: '6px' }}>
                            <img
                              src={allergyData.allergy_report.parent_signature}
                              alt="Parent Signature"
                              style={{ maxWidth: '100%', maxHeight: '60px', display: 'block' }}
                            />
                          </div>
                          {allergyData.allergy_report.parent_signature_date && (
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>
                              Signed: {formatDisplayDate(allergyData.allergy_report.parent_signature_date)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>
                          Not signed
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                        Supervisor Signature:
                      </div>
                      {allergyData.allergy_report.supervisor_signature ? (
                        <div>
                          <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '8px', background: '#f9fafb', marginBottom: '6px' }}>
                            <img
                              src={allergyData.allergy_report.supervisor_signature}
                              alt="Supervisor Signature"
                              style={{ maxWidth: '100%', maxHeight: '60px', display: 'block' }}
                            />
                          </div>
                          {allergyData.allergy_report.supervisor_signature_date && (
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>
                              Signed: {formatDisplayDate(allergyData.allergy_report.supervisor_signature_date)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>
                          Not signed
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                {allergyData.allergy_report.supervisor_note && (
                  <div style={{ background: '#dbeafe', padding: '12px', borderRadius: '6px', marginTop: '12px', border: '1px solid #bfdbfe' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '6px', color: '#1e40af' }}>
                      Supervisor Notes:
                    </div>
                    <div
                      style={{ fontSize: '12px', color: '#374151' }}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(allergyData.allergy_report.supervisor_note) }}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="text-center p-4 text-muted">
            No allergy report found{studentName ? ` for ${studentName}` : ''}.
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default AllergyModal;
